<!--
  CollectorConflictCards — «وصلت من الليدر في آخر تزامن (HH:MM)» (handoff spec
  2.4, mockup Offline.dc.html). One card per tap the server answered with a
  `conflict`: the leader wins, the line is already pinned to the server's
  state, and the collector is told ONCE — the card stays until «تمام» (or
  «افتح {shop}», which also dismisses it). Red for a cancelled line, amber for
  everything else. Nothing here is retried.

  Phase 2 (collector-app P2-B): the cards live in the phone's `conflicts`
  store, so they survive a restart until dismissed. A REFUSAL is a card too —
  one per plan and reason («اليوم اتقفل — علامتك وصلت لليدر كملاحظة» over the
  lines it refused): a replay after a day offline can land while the phone
  is in a pocket, where a toast is lost.

  The shop's INVOICE has two cards of its own (collector-offline P1-B;
  collector/invoiceQueue.js files it from the phone, possibly hours later):
  `invoice_leader_closed` — the leader's figure stands, «تمام» — and
  `invoice_refused` — what he had filed, and «افتح {shop}» to file it again.
  They are about a shop, not a line: no `type`, no item.

  A shelf photo the server will never take has one too (collector-offline
  P2-A; collector/photoQueue.js): `photo_refused`, ONE card per line, its
  `count` grown by each further photo of that line — «افتح {shop}» to take it
  again from «بلّغ من الرف». It names the line (`productName`, `memberCode`)
  but no tap is behind it.
-->
<template>
  <section v-if="cards.length" class="c-conflicts">
    <h2 class="c-conflicts__heading">
      {{ t('collector.conflicts.heading', { time: syncTime }) }}
    </h2>
    <article
      v-for="card in cards"
      :key="card.id"
      class="c-conflict"
      :class="card.state === 'cancelled' ? 'c-conflict--red' : 'c-conflict--amber'"
      :data-state="card.state"
    >
      <div class="c-conflict__row">
        <svg
          v-if="card.state === 'cancelled'"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          class="c-conflict__icon"
        ><circle cx="12" cy="12" r="9" /><path d="M12 8v5" /><path d="M12 16h.01" /></svg>
        <svg
          v-else
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          class="c-conflict__icon"
        ><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9L2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
        <div class="c-conflict__text">
          <div class="c-conflict__title">
            {{ title(card) }}
          </div>
          <div class="c-conflict__body">
            {{ body(card) }}
          </div>
        </div>
      </div>
      <button
        v-if="opensShop(card)"
        type="button"
        class="c-reset c-conflict__btn c-conflict__btn--primary"
        @click="openShop(card)"
      >
        {{ t('collector.conflicts.openShop', { shop: card.shopName }) }}
      </button>
      <button
        v-else
        type="button"
        class="c-reset c-conflict__btn"
        @click="actions.dismissConflict(card.id)"
      >
        {{ t('collector.conflicts.ok') }}
      </button>
    </article>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCollectorActionsStore } from '@/collector/actions.js';
import { formatClock } from '@/collector/dayView.js';
import { INVOICE_CARD, refusedInvoiceTitle } from '@/collector/invoiceQueue.js';
import { PHOTO_CARD } from '@/collector/photoQueue.js';
import { useCurrency } from '@/composables/useCurrency.js';

const emit = defineEmits(['open-shop']);

const { t, te } = useI18n();
const actions = useCollectorActionsStore();
const { formatAmount } = useCurrency();

// Her answers are told apart (CollectorAnswerCards): they are not a leader's decision.
const cards = computed(() => actions.conflicts.filter((card) => card.state !== 'answered'));
const syncTime = computed(() => formatClock(cards.value[cards.value.length - 1]?.syncedAt) ?? '');

const KNOWN = [
  'cancelled',
  'rejected',
  'moved',
  'verified',
  'changed',
  'question_open',
  'marked',
  'refused',
  INVOICE_CARD.LEADER_CLOSED,
  INVOICE_CARD.REFUSED,
  PHOTO_CARD.REFUSED,
];
const REFUSAL_REASONS = [
  'plan_closed',
  'plan_cancelled',
  'plan_not_started',
  'not_collector',
  'photo_cap',
  'invalid_photo',
  'no_answered_question',
  'not_found',
  'invalid',
  'error',
];
/** Lines named on a refusal card before «و N كمان». */
const REFUSED_LISTED = 3;
const OPENS_SHOP = new Set(['rejected', 'changed', 'marked', INVOICE_CARD.REFUSED, PHOTO_CARD.REFUSED]);
/** Cards about a shop's invoice: no line and no tap behind them. */
const ABOUT_INVOICE = new Set([INVOICE_CARD.LEADER_CLOSED, INVOICE_CARD.REFUSED]);

function stateOf(card) {
  return KNOWN.includes(card.state) ? card.state : 'changed';
}

function item(card) {
  if (!card.productName) return t('collector.conflicts.itemFallback');
  return card.memberCode
    ? t('collector.conflicts.item', { product: card.productName, code: card.memberCode })
    : card.productName;
}

function params(card) {
  const outcome = card.currentOutcome && te(`collector.outcome.${card.currentOutcome}`)
    ? t(`collector.outcome.${card.currentOutcome}`)
    : '';
  return {
    item: item(card),
    tap: t(`collector.tap.${card.type}`),
    shop: card.shopName ?? '',
    reason: card.reason ?? '',
    outcome,
  };
}

/** A card whose shop the day no longer names still reads as a sentence. */
function shopOf(card) {
  return card.shopName ?? t('collector.shops.noShop');
}

function invoiceTitle(card) {
  if (card.state === INVOICE_CARD.REFUSED) return refusedInvoiceTitle({ ...card, shopName: shopOf(card) }, t, te);
  return t(`collector.conflicts.${card.state}.title`, { shop: shopOf(card) });
}

/** What he had filed: the amount, or that the shop gave no paper. */
function invoiceBody(card) {
  if (card.state !== INVOICE_CARD.REFUSED) return t(`collector.conflicts.${card.state}.body`);
  if (card.noPaper || card.amountCents == null) return t('collector.conflicts.invoice_refused.bodyNoPaper');
  return t('collector.conflicts.invoice_refused.body', { amount: formatAmount(card.amountCents, { trimZeros: true }) });
}

/** One photo, or «N صور» when more of that line's photos were refused. */
function photoTitle(card) {
  return card.count > 1
    ? t('collector.conflicts.photo_refused.titleMany', { n: card.count })
    : t('collector.conflicts.photo_refused.title');
}

function title(card) {
  if (ABOUT_INVOICE.has(card.state)) return invoiceTitle(card);
  if (card.state === PHOTO_CARD.REFUSED) return photoTitle(card);
  if (stateOf(card) === 'refused') {
    return t(`collector.refused.${REFUSAL_REASONS.includes(card.reason) ? card.reason : 'error'}`);
  }
  return t(`collector.conflicts.${stateOf(card)}.title`, params(card));
}

/** «بلوزة حريمي قطن · كود 1023، شنطة يد جلد · كود 1187 و2 كمان» */
function refusedItems(card) {
  const items = (card.items ?? []).map((entry) => item(entry));
  const listed = items.slice(0, REFUSED_LISTED).join('، ');
  const more = items.length - REFUSED_LISTED;
  return more > 0 ? t('collector.conflicts.refusedMore', { items: listed, n: more }) : listed;
}

function body(card) {
  const state = stateOf(card);
  if (ABOUT_INVOICE.has(state)) return invoiceBody(card);
  if (state === PHOTO_CARD.REFUSED) return t('collector.conflicts.photo_refused.body', { item: item(card) });
  if (state === 'refused') return refusedItems(card);
  if (state === 'rejected' && !card.reason) return t('collector.conflicts.rejected.bodyNoReason', params(card));
  return t(`collector.conflicts.${state}.body`, params(card));
}

function opensShop(card) {
  return OPENS_SHOP.has(stateOf(card)) && card.shopKey != null && card.shopName;
}

function openShop(card) {
  actions.dismissConflict(card.id);
  // The card's own plan rides along: a filing of his other plan of today opens ITS shop.
  emit('open-shop', card.shopKey, card.planId ?? null);
}
</script>

<style scoped>
.c-conflicts {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 14px 16px 0;
}

.c-conflicts__heading {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--c-muted);
}

.c-conflict {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: var(--c-card);
  border-radius: var(--c-radius-row);
}

.c-conflict--red {
  border: 2px solid var(--c-red-border);
}

.c-conflict--red .c-conflict__icon {
  color: var(--c-red-text);
}

.c-conflict--amber {
  border: 2px solid var(--c-amber-border);
}

.c-conflict--amber .c-conflict__icon {
  color: var(--c-amber-text);
}

.c-conflict__row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.c-conflict__icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.c-conflict__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.c-conflict__title {
  font-size: 15px;
  font-weight: 700;
}

.c-conflict__body {
  font-size: 13px;
  color: var(--c-ink-soft);
}

.c-conflict__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
  color: var(--c-indigo);
  font-size: 15px;
  font-weight: 700;
}

.c-conflict__btn--primary {
  background: var(--c-indigo);
  color: #ffffff;
}
</style>
